/** Viewer identity for ประกาศ — session JWT only, never Graph app token. */

function norm(v) {
  return String(v ?? '')
    .trim()
    .toLowerCase();
}

function announcementViewerKeys(decoded, emp) {
  if (!decoded) return [];
  const keys = [
    emp?.employeeCode,
    emp?.username,
    emp?.email,
    emp?.nameTh,
    decoded.initials,
    decoded.sAMAccountName,
    decoded.UserCode,
    decoded.username,
    decoded.userPrincipalName,
    decoded.mail,
    decoded.email,
    decoded.Email,
    decoded.preferred_username,
    decoded.unique_name,
    decoded.displayName,
  ]
    .map(norm)
    .filter((k) => k && k !== 'undefined' && k !== 'null');
  return [...new Set(keys)];
}

/** Ack gate may block only after this user is matched in the loaded employee list. */
function announcementIdentityReady(employees, emp) {
  return Array.isArray(employees) && employees.length > 0 && emp != null;
}

function hasAcknowledged(viewers, keys) {
  if (!viewers || !keys?.length) return false;
  const seen = new Set(
    String(viewers)
      .split(',')
      .map(norm)
      .filter(Boolean)
  );
  return keys.some((k) => seen.has(k));
}

function creatorDisplayName(emp, decoded) {
  const fromJwt = [decoded?.firstName, decoded?.lastName].filter(Boolean).join(' ').trim();
  return emp?.nameTh || decoded?.displayName || decoded?.name || fromJwt || 'ไม่ทราบชื่อ';
}

const VIEWER_JUNK = new Set(['', 'undefined', 'null', 'nan']);

function splitThaiName(nameTh) {
  const parts = String(nameTh || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length <= 1) return { givenName: parts[0] || '', surname: '' };
  return { givenName: parts[0], surname: parts.slice(1).join(' ') };
}

const EMPTY_BUCKETS = {
  code: new Map(),
  username: new Map(),
  email: new Map(),
  name: new Map(),
  en: new Map(),
  nickEn: new Map(),
};
const bucketCache = new WeakMap();

function putBucket(map, value, emp) {
  const key = norm(value);
  if (!key || VIEWER_JUNK.has(key) || map.has(key)) return;
  map.set(key, emp);
}

function viewerBuckets(employees) {
  if (!employees) return EMPTY_BUCKETS;
  const cached = bucketCache.get(employees);
  if (cached) return cached;
  const buckets = {
    code: new Map(),
    username: new Map(),
    email: new Map(),
    name: new Map(),
    en: new Map(),
    nickEn: new Map(),
  };
  employees.forEach((emp) => {
    putBucket(buckets.code, emp?.employeeCode, emp);
    putBucket(buckets.username, emp?.username, emp);
    putBucket(buckets.email, emp?.email, emp);
    putBucket(buckets.name, emp?.nameTh, emp);
    putBucket(buckets.en, `${emp?.firstName || ''} ${emp?.lastName || ''}`.trim(), emp);
    putBucket(buckets.nickEn, emp?.nickNameEn, emp);
  });
  bucketCache.set(employees, buckets);
  return buckets;
}

function lookupViewer(token, buckets) {
  const key = norm(token);
  return (
    buckets.code.get(key) ||
    buckets.username.get(key) ||
    buckets.email.get(key) ||
    buckets.name.get(key) ||
    buckets.en.get(key) ||
    buckets.nickEn.get(key) ||
    null
  );
}

/** Map viewers CSV (employee code, username, name, or the literal "undefined") to unique people. */
function resolveDocumentViewers(viewers, employees) {
  const tokens = String(viewers ?? '')
    .split(',')
    .map((token) => token.trim())
    .filter((token) => !VIEWER_JUNK.has(norm(token)));
  const buckets = viewerBuckets(employees);
  const seen = new Set();
  const rows = [];
  tokens.forEach((token) => {
    const emp = lookupViewer(token, buckets);
    let id = `raw:${norm(token)}`;
    if (emp?.employeeCode) id = `emp:${norm(emp.employeeCode)}`;
    else if (emp?.employeeId) id = `id:${emp.employeeId}`;
    if (seen.has(id)) return;
    seen.add(id);
    const { givenName, surname } = splitThaiName(emp?.nameTh);
    rows.push({
      key: id,
      employeeCode: emp?.employeeCode ? String(emp.employeeCode).trim() : '',
      nickname: String(emp?.nickname || '').trim(),
      givenName,
      surname,
      nameTh: String(emp?.nameTh || '').trim(),
      label: emp ? '' : token,
    });
  });
  return rows;
}

function appendViewerKeys(viewers, keys) {
  let next = String(viewers ?? '');
  (keys || []).forEach((key) => {
    const token = String(key ?? '').trim();
    if (!token || token.toLowerCase() === 'undefined' || token.toLowerCase() === 'null') return;
    if (hasAcknowledged(next, [token])) return;
    next = next.trim() ? `${next.trim()}, ${token}` : token;
  });
  return next;
}

export {
  norm,
  hasAcknowledged,
  appendViewerKeys,
  creatorDisplayName,
  resolveDocumentViewers,
  announcementViewerKeys,
  announcementIdentityReady,
};
